import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/desenho.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const ManutencaoVistoriaManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_vistoria: '',
    id_manutencao: '',
    data_vistoria: '',
    itens_avaliados: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);
  const [manutencaoSearch, setManutencaoSearch] = useState('');
  const [manutencoes, setManutencoes] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchManutencoes = useCallback(async () => {
    try {
      const response = await api.get('/manutencao');
      setManutencoes(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar manutenções:', error);
    }
  }, []);

  const manutencoesFiltradas = useMemo(() => {
    if (!manutencaoSearch) return manutencoes;
    const searchLower = manutencaoSearch.toLowerCase();
    return manutencoes.filter(m =>
      String(m.id_manutencao).toLowerCase().includes(searchLower) ||
      (m.descr_manutencao && m.descr_manutencao.toLowerCase().includes(searchLower)) ||
      (m.nome_campanha && m.nome_campanha.toLowerCase().includes(searchLower))
    );
  }, [manutencoes, manutencaoSearch]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/manutencao-vistoria');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchManutencoes();
  }, [verificarAPI, fetchManutencoes]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'vistoria de manutenção', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/manutencao-vistoria/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'vistoria de manutenção', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'vistoria de manutenção');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'vistoria de manutenção', { id });
    try {
      const response = await api.delete(`/manutencao-vistoria/${id}`);
      logarSucesso('Excluir', 'vistoria de manutenção', response.data);
      setDados(dadosAtuais => dadosAtuais.filter(item => item.id_vistoria !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'vistoria de manutenção');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id_vistoria, data);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_vistoria}"?`)) {
          excluirRegistro(data.id_vistoria);
        }
      },
      headerSort: false
    },
    {
      title: 'ID Vistoria',
      field: 'id_vistoria',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string'
    },
    {
      title: 'ID Manutenção',
      field: 'id_manutencao',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Data Vistoria',
      field: 'data_vistoria',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar data...',
      sorter: 'date',
      formatter: (cell) => {
        const value = cell.getValue();
        if (!value) return '';

        // Se já estiver em formato DD/MM/AAAA, retorna direto
        if (value.includes('/')) return value;

        // Converte de AAAA-MM-DD para DD/MM/AAAA
        const date = new Date(value + 'T00:00:00Z');
        if (isNaN(date.getTime())) return value;

        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
      },
      editor: (cell, onRendered, success, cancel) => {
        const input = document.createElement("input");
        input.setAttribute("type", "text");
        input.setAttribute("placeholder", "DD/MM/YYYY");
        input.style.padding = "4px";
        input.style.width = "100%";
        input.style.boxSizing = "border-box";

        const value = cell.getValue();
        if (value) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const day = String(date.getUTCDate()).padStart(2, '0');
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const year = date.getUTCFullYear();
              input.value = `${day}/${month}/${year}`;
            } else {
              input.value = value;
            }
          } catch (e) {
            input.value = value;
          }
        }

        onRendered(() => {
          input.focus();
          input.style.height = "100%";
        });

        const handleDateInput = (e) => {
          const input = e.target;
          let value = input.value.replace(/\D/g, '');
          let formattedValue = '';
          if (value.length > 0) {
            formattedValue = value.substring(0, 2);
          }
          if (value.length > 2) {
            formattedValue += '/' + value.substring(2, 4);
          }
          if (value.length > 4) {
            formattedValue += '/' + value.substring(4, 8);
          }
          input.value = formattedValue;
        };

        input.addEventListener('input', handleDateInput);

        const onChange = () => {
          if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [day, month, year] = input.value.split('/');
            success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
          } else {
            success(input.value);
          }
        };

        input.addEventListener("change", onChange);
        input.addEventListener("blur", onChange);

        return input;
      }
    },
    {
      title: 'Itens Avaliados',
      field: 'itens_avaliados',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar itens...',
      sorter: 'string',
      editor: 'input'
    }
  ], [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/manutencao-vistoria');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_vistoria'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_vistoria, id_manutencao, data_vistoria, itens_avaliados } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_vistoria) {
      camposObrigatorios.push('ID Vistoria');
      erros.push('id_vistoria');
    }
    if (!id_manutencao) {
      camposObrigatorios.push('ID Manutenção');
      erros.push('id_manutencao');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_vistoria,
      id_manutencao,
      data_vistoria: data_vistoria === '' ? null : data_vistoria,
      itens_avaliados
    };

    logarRequisicao('Adicionar', 'vistoria de manutenção', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/manutencao-vistoria', dataToSend);
      logarSucesso('Adicionar', 'vistoria de manutenção', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_vistoria: '', id_manutencao: '', data_vistoria: '', itens_avaliados: '' });
      setManutencaoSearch('');
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'vistoria de manutenção');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const handleDataVistoriaChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito

    if (value.length <= 2) {
      // Apenas DD
      e.target.value = value;
    } else if (value.length <= 4) {
      // DD/MM
      e.target.value = `${value.slice(0, 2)}/${value.slice(2)}`;
    } else if (value.length <= 8) {
      // DD/MM/AAAA
      e.target.value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    } else {
      // Limita a 8 dígitos (DD/MM/AAAA)
      e.target.value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    }

    setFormData(prev => ({ ...prev, data_vistoria: e.target.value }));

    // Remove o erro do campo quando ele for preenchido
    if (e.target.value) {
      setCamposComErro(prev => prev.filter(campo => campo !== 'data_vistoria'));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'manutencao_vistoria.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Vistoria de Manutenção</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_manutencao_vistoria</p>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_vistoria">ID Vistoria:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_vistoria" name="id_vistoria" value={formData.id_vistoria} onChange={handleInputChange} placeholder="Ex: 1" disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_vistoria') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="id_manutencao">ID Manutenção:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              list="manutencoes-list"
              value={manutencaoSearch}
              onChange={(e) => {
                setManutencaoSearch(e.target.value);
                const match = manutencoes.find(m => `${m.id_manutencao} - ${m.descr_manutencao || m.nome_campanha || ''}` === e.target.value);
                if (match) {
                  setFormData(prev => ({ ...prev, id_manutencao: match.id_manutencao }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_manutencao'));
                }
              }}
              placeholder="Buscar manutenção..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_manutencao') ? 'input-error' : ''}
            />
            <datalist id="manutencoes-list">
              {manutencoesFiltradas.map(m => (
                <option key={m.id_manutencao} value={`${m.id_manutencao} - ${m.descr_manutencao || m.nome_campanha || ''}`} />
              ))}
            </datalist>
            <input type="hidden" name="id_manutencao" value={formData.id_manutencao} />
          </div>
          <div className="form-group">
            <label htmlFor="data_vistoria">Data vistoria:</label>
            <input type="text" id="data_vistoria" name="data_vistoria" value={formData.data_vistoria} onChange={handleDataVistoriaChange} placeholder="DD/MM/AAAA" disabled={loading || apiStatus !== 'connected'} maxLength="10" />
          </div>
          <div className="form-group">
            <label htmlFor="itens_avaliados">Itens avaliados:</label>
            <input type="text" id="itens_avaliados" name="itens_avaliados" value={formData.itens_avaliados} onChange={handleInputChange} placeholder="Ex: itens" disabled={loading || apiStatus !== 'connected'} />
          </div>

          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default ManutencaoVistoriaManager;